/**
 * Unified Monetization Config — FALLBACK only
 *
 * Backend /api/monetization/packages is the source of truth.
 * These values are used as fallback when backend is unreachable.
 *
 * Regional pricing (SD, GULF, DEFAULT), subscription plans,
 * boost costs, translations, A/B test variants.
 */

// ═══════════════════════════════════════════════════════
// COIN PACKAGES — region-aware fallback
// ═══════════════════════════════════════════════════════
export const COIN_PACKAGES = {
  SD: [
    { id: 'pkg_10', coins: 10, price: 1500, currency: 'SDG', labelKey: 'starter' },
    { id: 'pkg_25', coins: 25, price: 3500, currency: 'SDG', labelKey: 'popular', badge: 'popular' },
    { id: 'pkg_60', coins: 60, price: 8000, currency: 'SDG', labelKey: 'best_value', badge: 'best_value', recommended: true },
  ],
  GULF: [
    { id: 'pkg_10', coins: 10, price: 7, currency: 'AED', labelKey: 'starter' },
    { id: 'pkg_30', coins: 30, price: 15, currency: 'AED', labelKey: 'popular', badge: 'popular' },
    { id: 'pkg_80', coins: 80, price: 35, currency: 'AED', labelKey: 'best_value', badge: 'best_value', recommended: true },
  ],
  DEFAULT: [
    { id: 'pkg_10', coins: 10, price: 1.99, currency: 'USD', labelKey: 'starter' },
    { id: 'pkg_30', coins: 30, price: 4.99, currency: 'USD', labelKey: 'popular', badge: 'popular' },
    { id: 'pkg_80', coins: 80, price: 9.99, currency: 'USD', labelKey: 'best_value', badge: 'best_value', recommended: true },
  ],
};

// ═══════════════════════════════════════════════════════
// SUBSCRIPTION PLANS — Pro & Power Seller
// ═══════════════════════════════════════════════════════
export const SUBSCRIPTION_PLANS = {
  SD: [
    {
      id: 'pro', price: 6000, currency: 'SDG',
      coinsPerMonth: 40, freeBoostsPerWeek: 2,
      features: ['priority_rank', 'weekly_auto_boost', 'pro_badge'],
    },
    {
      id: 'power_seller', price: 12000, currency: 'SDG',
      coinsPerMonth: 100, freeBoostsPerWeek: 14,
      features: ['top_exposure', 'analytics', 'unlimited_boosts', 'power_badge'],
    },
  ],
  GULF: [
    {
      id: 'pro', price: 12, currency: 'AED',
      coinsPerMonth: 40, freeBoostsPerWeek: 2,
      features: ['priority_rank', 'weekly_auto_boost', 'pro_badge'],
    },
    {
      id: 'power_seller', price: 25, currency: 'AED',
      coinsPerMonth: 100, freeBoostsPerWeek: 14,
      features: ['top_exposure', 'analytics', 'unlimited_boosts', 'power_badge'],
    },
  ],
  DEFAULT: [
    {
      id: 'pro', price: 3.99, currency: 'USD',
      coinsPerMonth: 40, freeBoostsPerWeek: 2,
      features: ['priority_rank', 'weekly_auto_boost', 'pro_badge'],
    },
    {
      id: 'power_seller', price: 8.99, currency: 'USD',
      coinsPerMonth: 100, freeBoostsPerWeek: 14,
      features: ['top_exposure', 'analytics', 'unlimited_boosts', 'power_badge'],
    },
  ],
};

// ═══════════════════════════════════════════════════════
// BOOST COSTS — regional fallback
// ═══════════════════════════════════════════════════════
export const BOOST_COSTS = {
  SD: {
    BOOST_HIGHLIGHT_24H: 3, AUTO_REFRESH_24H: 8,
    BADGE_URGENT_7D: 6, BOOST_TOP_CATEGORY_24H: 10, BOOST_HOMEPAGE_24H: 18,
  },
  GULF: {
    BOOST_HIGHLIGHT_24H: 6, AUTO_REFRESH_24H: 15,
    BADGE_URGENT_7D: 12, BOOST_TOP_CATEGORY_24H: 20, BOOST_HOMEPAGE_24H: 35,
  },
  DEFAULT: {
    BOOST_HIGHLIGHT_24H: 5, AUTO_REFRESH_24H: 12,
    BADGE_URGENT_7D: 10, BOOST_TOP_CATEGORY_24H: 18, BOOST_HOMEPAGE_24H: 30,
  },
};

// ═══════════════════════════════════════════════════════
// A/B TEST VARIANTS
// ═══════════════════════════════════════════════════════
export const AB_VARIANTS = {
  A: { packageHighlight: 'best_value', showSubscriptionFirst: false, ctaCopy: 'best_value' },
  B: { packageHighlight: 'popular', showSubscriptionFirst: true, ctaCopy: 'most_popular' },
};

export function getMonetizationVariant(userId) {
  if (!userId) return 'A';
  const hash = userId.split('').reduce((a, c) => a + c.charCodeAt(0), 0);
  return hash % 2 === 0 ? 'A' : 'B';
}

// ═══════════════════════════════════════════════════════
// TRANSLATIONS
// ═══════════════════════════════════════════════════════
export const MONETIZATION_LABELS = {
  starter: { en: 'Starter', ar: 'البداية' },
  popular: { en: 'Most Popular', ar: 'الأكثر شيوعاً' },
  best_value: { en: 'Best Value', ar: 'أفضل قيمة' },
  most_popular: { en: 'Most Popular', ar: 'الأكثر شيوعاً' },
  pro_seller: { en: 'Pro Seller', ar: 'بائع محترف' },

  // Plan names
  pro: { en: 'Pro', ar: 'برو' },
  power_seller: { en: 'Power Seller', ar: 'بائع متميز' },

  // Features
  priority_rank: { en: 'Priority ranking in search', ar: 'أولوية في نتائج البحث' },
  weekly_auto_boost: { en: '2 free boosts/week', ar: 'تعزيزان مجانيان/أسبوع' },
  pro_badge: { en: 'Pro seller badge', ar: 'شارة بائع محترف' },
  top_exposure: { en: 'Top exposure in category', ar: 'ظهور أعلى في القسم' },
  analytics: { en: 'Performance analytics', ar: 'تحليلات الأداء' },
  unlimited_boosts: { en: 'Unlimited boosts (fair use)', ar: 'تعزيزات غير محدودة' },
  power_badge: { en: 'Power Seller badge', ar: 'شارة بائع متميز' },
  cv_builder_unlock: { en: 'Premium CV builder', ar: 'منشئ سيرة ذاتية احترافي' },
  premium_cv_templates: { en: 'Premium CV templates', ar: 'قوالب سيرة ذاتية مميزة' },

  // UI strings
  buy_coins: { en: 'Buy Coins', ar: 'شراء عملات' },
  upgrade_to_pro: { en: 'Upgrade to Pro', ar: 'ترقية إلى برو' },
  boost_my_ad: { en: 'Boost My Ad', ar: 'عزّز إعلاني' },
  need_more_coins: { en: 'Need more coins?', ar: 'تحتاج عملات إضافية؟' },
  low_coins_warning: { en: "You're running low on coins", ar: 'رصيدك منخفض' },
  save_with_pro: { en: 'Save more with Pro', ar: 'وفّر أكثر مع برو' },
  you_spend_more: { en: 'You spend more than Pro costs — upgrade and save', ar: 'أنت تنفق أكثر من تكلفة برو — ترقَّ ووفّر' },
  per_month: { en: '/month', ar: '/شهر' },
  coins_per_month: { en: 'coins/month', ar: 'عملة/شهر' },
  waiting_confirmation: { en: 'Waiting for payment confirmation', ar: 'في انتظار تأكيد الدفع' },
  payment_successful: { en: 'Payment successful', ar: 'تم الدفع بنجاح' },
  pay_with_bankak: { en: 'Pay with Bankak', ar: 'ادفع عبر بنكك' },
  pay_by_card: { en: 'Pay by card', ar: 'ادفع بالبطاقة' },
  use_coins: { en: 'Use Coins', ar: 'استخدم العملات' },
  get_student_pro: { en: 'Get Student Pro', ar: 'احصل على طالب محترف' },
  retry_payment: { en: 'Retry payment', ar: 'إعادة المحاولة' },
  recommended: { en: 'Recommended', ar: 'الأفضل' },
  monthly_spend_label: { en: 'You spent {x} coins this month', ar: 'أنفقت {x} عملة هذا الشهر' },
};

// ═══════════════════════════════════════════════════════
// CURRENCY FORMATTING
// ═══════════════════════════════════════════════════════
const CURRENCY_SYMBOLS = {
  SDG: { en: 'SDG', ar: 'ج.س' },
  USD: { en: '$', ar: '$' },
  AED: { en: 'AED', ar: 'د.إ' },
};

export function formatPrice(amount, currency, lang = 'en') {
  const isAr = lang?.startsWith('ar');
  const sym = CURRENCY_SYMBOLS[currency] || { en: currency, ar: currency };
  const formatted = typeof amount === 'number' ? amount.toLocaleString() : amount;
  if (currency === 'USD') {
    return `${sym[isAr ? 'ar' : 'en']}${formatted}`;
  }
  return isAr ? `${formatted} ${sym.ar}` : `${formatted} ${sym.en}`;
}

// ═══════════════════════════════════════════════════════
// HELPERS
// ═══════════════════════════════════════════════════════

export function getLabel(key, lang = 'en') {
  const entry = MONETIZATION_LABELS[key];
  if (!entry) return key;
  return lang?.startsWith('ar') ? entry.ar : entry.en;
}

export function getRegion(country) {
  if (!country) return 'DEFAULT';
  const c = country.toUpperCase();
  if (c === 'SD' || c === 'SDN' || c === 'SUDAN') return 'SD';
  if (['SA', 'AE', 'KW', 'QA', 'BH', 'OM'].includes(c)) return 'GULF';
  return 'DEFAULT';
}

export function getPackagesForRegion(country) {
  const region = getRegion(country);
  return COIN_PACKAGES[region] || COIN_PACKAGES.DEFAULT;
}

export function getPlansForRegion(country) {
  const region = getRegion(country);
  return SUBSCRIPTION_PLANS[region] || SUBSCRIPTION_PLANS.DEFAULT;
}

export function suggestBestPackage(requiredCoins, country) {
  const pkgs = getPackagesForRegion(country);
  // Find the smallest package that covers the need
  const covering = pkgs.filter(p => p.coins >= requiredCoins).sort((a, b) => a.coins - b.coins);
  return covering[0] || pkgs[pkgs.length - 1];
}

/**
 * Convert backend package format to frontend format
 */
export function normalizePackage(pkg) {
  return {
    id: pkg.package_id || pkg.id,
    coins: pkg.coins,
    price: pkg.price,
    currency: pkg.currency,
    labelKey: pkg.label_key || pkg.labelKey || 'starter',
    // Use label_key as badge key for proper i18n translation
    badge: pkg.label_key || pkg.labelKey || null,
    badge_en: pkg.badge_en || null,
    badge_ar: pkg.badge_ar || null,
    recommended: !!(pkg.badge_en || pkg.recommended),
  };
}

/**
 * Convert backend plan format to frontend format
 */
export function normalizePlan(plan) {
  return {
    id: plan.plan_id || plan.id,
    price: plan.price,
    currency: plan.currency,
    coinsPerMonth: plan.monthly_coins || plan.coinsPerMonth || 0,
    freeBoostsPerWeek: plan.free_boosts_per_week || plan.freeBoostsPerWeek || 0,
    features: plan.features || [],
    name_en: plan.name_en,
    name_ar: plan.name_ar,
    badge_en: plan.badge_en,
    badge_ar: plan.badge_ar,
  };
}
